// Translator screen types — `translation_jobs` collection contract.
//
// Field names mirror transcriber-api (github.com/Dror-Bengal/nayto-translator,
// apps/api/README.md, v0.9.0) EXACTLY — these are load-bearing across
// api / worker / frontend.
//
// Endpoints (transcriber-api):
//   POST   /api/translations              — submit → 201 { _id, status, createdAt }
//   GET    /api/translations              — list (?all=true for admin/manager)
//                                           SLIM rows: never segments, summaries
//                                           or sourceUrl; privileged callers also
//                                           get ownerUid / owner / costEstimateUsd
//   GET    /api/translations/:id          — full document (segments, summaries,
//                                           sourceUrl) — owner or privileged
//   POST   /api/translations/:id/retry    — 200 { _id, status: 'pending', retryCount }
//   DELETE /api/translations/:id          — 204, no body
//
// Submit body is flat: `{ sourceType, sourceUrl, originalFilename? }`.
// All result fields live at the TOP LEVEL of the job doc (no `result`
// wrapper). Segments are a single array of per-row 4-language objects:
// each segment carries `start` + `end` + the four translation strings
// inline.
//
// Language order everywhere: en, he, hy, ru.

import type { Lang } from '@nayto/i18n';

/** `url` and `firebase` are what the browser submits; `upload` is a job an
 * admin created server-side via multipart POST /api/translations/upload
 * and only ever arrives in API responses. */
export type TranslationSourceType = 'url' | 'firebase' | 'upload';

/** The subset the browser is allowed to POST (see TranslatorScreen's
 * detectSourceType). */
export type BrowserTranslationSourceType = Exclude<TranslationSourceType, 'upload'>;

export type TranslationStatus = 'pending' | 'claimed' | 'processing' | 'complete' | 'failed';

export const TRANSLATION_TERMINAL_STATUSES: ReadonlySet<TranslationStatus> =
  new Set<TranslationStatus>(['complete', 'failed']);

export const TRANSLATION_ACTIVE_STATUSES: ReadonlySet<TranslationStatus> =
  new Set<TranslationStatus>(['pending', 'claimed', 'processing']);

export const TRANSLATION_LANGS: readonly Lang[] = ['en', 'he', 'hy', 'ru'];

/** Failure buckets. The first four are the CONTRACT.md Appendix A set
 * (api ≤ 0.10); `unsupported_source | unsupported_media |
 * download_timeout | source_unavailable | unknown` arrive with api R-09.
 * Absent / unknown category → the UI shows errorMessage as-is. */
export type TranslationErrorCategory =
  | 'user_fixable'
  | 'transient'
  | 'infrastructure'
  | 'unsupported_source'
  | 'unsupported_media'
  | 'download_timeout'
  | 'source_unavailable'
  | 'unknown';

/** One transcript row. `start`/`end` are seconds from the source's t=0;
 * the four optional language strings are translations of the same span.
 * Per-lang strings are optional because the backend may not populate
 * all four (early failure, partial chunk, etc.). */
/** Diarization output (transcriber 0.18.0, T-01). `gender` is the
 * model's guess and is explicitly nullable — it drives grammatical
 * gender in Hebrew and Russian labels, where guessing wrong is worse
 * than not guessing, so `null` reads as the masculine default. */
export type SpeakerGender = 'm' | 'f';

/** The speaker of one segment. `id` is 'S1', 'S2', … in first-heard
 * order — per CHUNK, not per job, which is what `speakersReconciled`
 * is about. */
export interface SegmentSpeaker {
  id: string;
  gender: SpeakerGender | null;
}

export interface TranslationSegment {
  start: number;
  end?: number;
  en?: string;
  he?: string;
  hy?: string;
  ru?: string;
  /** Absent on jobs transcribed before 0.18.0. */
  speaker?: SegmentSpeaker;
}

/** One speaker across the whole job. `name` is set when the pipeline
 * could identify who it is; when it is null the UI numbers them. */
export interface TranslationSpeaker {
  id: string;
  gender: SpeakerGender | null;
  name: string | null;
}

/** R-16 (api 0.16.0): a chunk that failed all three attempts. A
 * `complete` job may carry a few; a `failed` job with `chunks_failed`
 * keeps the good chunks' segments. Retry re-transcribes only these. */
export interface TranslationFailedChunk {
  index: number;
  fromSeconds: number;
  toSeconds: number;
  errorDetail?: string | null;
}

/** Per-language map (used for `summaries`). Values optional because
 * the backend may partially populate. */
export type TranslationLangMap<T> = Partial<Record<Lang, T>>;

/** Owner block attached to list rows and documents for admin/manager
 * callers (one app_users lookup per page). A deleted account leaves
 * `uid` only. Display: displayName ?? email ?? phoneNumber ?? uid.
 * `email` arrives with transcriber-api R-06; optional until then. */
export interface TranslationJobOwner {
  uid: string;
  displayName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

/** Job doc — all fields live at the top level (no `result` wrapper).
 * The SAME shape describes a list row and a full document; the fields
 * marked "detail only" are absent from list rows and arrive with
 * GET /api/translations/:id. */
export interface TranslationJob {
  _id: string;
  /** Privileged (admin/manager) callers only. */
  ownerUid?: string;
  /** Always null on transcriber-api (phone auth, no e-mail). Kept for
   * shape compatibility; never displayed. */
  ownerEmail?: string | null;
  /** Privileged callers only. */
  owner?: TranslationJobOwner | null;
  /** Flat source fields per the final cross-repo contract. */
  sourceType: TranslationSourceType;
  /** Detail only — list rows never carry the source URL. */
  sourceUrl?: string;
  /** Optional filename when sourceType === 'firebase'. */
  originalFilename?: string | null;

  // Pipeline state
  status: TranslationStatus;
  workerId?: string | null;
  claimedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  lastHeartbeatAt?: string | null;
  retryCount: number;
  errorCategory?: TranslationErrorCategory | null;
  /** One-line, user-facing. */
  errorMessage?: string | null;
  /** Raw technical detail (stderr tail etc.). Detail only (GET /:id),
   * api R-09+. Shown collapsed under "Technical details". */
  errorDetail?: string | null;
  /** R-14: the server's verdict on whether a retry can succeed. Absent
   * on older api versions → the UI falls back to `errorCategory`. */
  retryable?: boolean | null;

  // Progress (translator updates while processing)
  numChunks?: number | null;
  chunksCompleted?: number | null;
  progressMessage?: string | null;
  /** R-16: chunks that failed all attempts (list + detail). Empty on a
   * clean run. The UI shows a gap row per entry inside the transcript. */
  chunksFailed?: TranslationFailedChunk[] | null;
  /** R-12: position in the pending queue (1 = next). Pending jobs only. */
  queuePosition?: number | null;
  /** R-12: true while `segments` holds the chunks finished so far and the
   * job is still processing. Cleared on completion. */
  partial?: boolean;
  /** R-12: estimates made at submission (from the probed duration). */
  estimatedProcessingSeconds?: number | null;
  estimatedCostUsd?: number | null;

  // Source metadata (translator probes)
  durationSeconds?: number | null;

  // Result fields (top-level, populated incrementally; complete on `complete`)
  title?: string | null;
  englishTitle?: string | null;
  participants?: string[];
  /** ~3-sentence summary per language. Detail only. */
  summaries?: TranslationLangMap<string>;
  /** Single timestamped array — each row carries all 4 lang strings.
   * Detail only. */
  segments?: TranslationSegment[];
  /** Detail only. Absent on jobs from before 0.18.0. */
  speakers?: TranslationSpeaker[];
  /**
   * Whether S1 in one chunk is the same person as S1 in the next.
   *
   *   true  — reconciled; the numbering holds across the whole job.
   *   false — NOT reconciled; the numbering may restart between chunks,
   *           so the labels are still shown but with a warning.
   *   null  — the question was not answered. Labels are suppressed
   *           entirely: a speaker label nobody vouches for is worse
   *           than no label, because a reader cannot tell the
   *           difference by looking.
   *
   * Absent (not null) on pre-0.18.0 jobs, which behave the same way.
   */
  speakersReconciled?: boolean | null;
  /** Visible to admin/manager only — gated in the UI. */
  costEstimateUsd?: number | null;
  model?: string;

  createdAt: string;
  updatedAt: string;
}

export interface TranslationsListResponse {
  jobs: TranslationJob[];
  /** Total rows in scope (not just this page). */
  total?: number;
  /** 'all' when `?all=true` was honoured (admin/manager), else 'own'. */
  scope?: 'own' | 'all';
  /** Cursor for the next page: createdAt of the oldest row when the page
   * is full, else null. Pass back as `?before=`. */
  nextBefore?: string | null;
}

export interface TranslationSubmitBody {
  sourceType: BrowserTranslationSourceType;
  sourceUrl: string;
  /** Display label for Firebase-uploaded sources. */
  originalFilename?: string;
}

/** Body shape for the structured 409 errors on retry/delete. */
export interface TranslationConflictBody {
  error: 'invalid_status' | 'retry_exhausted' | string;
  current?: TranslationStatus;
}

/** Firebase signed-URL detector — must match the processor validator
 * byte-for-byte. Anchors + escaped dots; `?` and `&token=` query
 * structure isn't validated beyond presence so the frontend stays
 * tolerant of backend tweaks. */
export const FIREBASE_SIGNED_URL_RE =
  /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/[^?]+\?[^#]*&?token=[a-f0-9-]+/;

export const MAX_ACTIVE_TRANSLATION_JOBS = 3;
export const TRANSLATION_RETRY_LIMIT = 3;
export const TRANSLATION_POLL_INTERVAL_MS = 15_000;
